/* eslint-disable react/no-unescaped-entities, react-hooks/set-state-in-effect -- texte FR + fetch-on-mount (loadBilling), fonctionnel et validé */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { billingAPI } from '../../lib/api';
import { AIQuotaCard } from '../../components/billing/AIQuotaCard';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
    CreditCard,
    Users,
    Building2,
    Receipt,
    CheckCircle,
    Loader2,
    ExternalLink,
    Calendar,
    ArrowRight,
} from 'lucide-react';

const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

function BillingPage() {
    const [searchParams] = useSearchParams();
    const [me, setMe] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openingPortal, setOpeningPortal] = useState(false);
    const [creatingCheckout, setCreatingCheckout] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [meRes, txRes] = await Promise.all([
                billingAPI.getMe(),
                billingAPI.getTransactions(50),
            ]);
            setMe(meRes.data);
            setTransactions(txRes.data?.transactions || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors du chargement de la facturation');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
        const sessionId = searchParams.get('session_id');
        if (sessionId) {
            billingAPI
                .getCheckoutStatus(sessionId)
                .then((res) => {
                    if (res.data.payment_status === 'paid') {
                        toast.success('Paiement effectué avec succès !');
                        loadData();
                    }
                })
                .catch(() => {});
        }
    }, [searchParams, loadData]);

    const handleOpenPortal = async () => {
        setOpeningPortal(true);
        try {
            const res = await billingAPI.openPortal();
            window.location.href = res.data.url;
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Impossible d\'ouvrir le portail Stripe');
            setOpeningPortal(false);
        }
    };

    const handleSubscribe = async () => {
        setCreatingCheckout(true);
        try {
            const res = await billingAPI.createCheckout();
            window.location.href = res.data.checkout_url;
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la création du paiement');
            setCreatingCheckout(false);
        }
    };

    if (loading || !me) {
        return (
            <div className="space-y-6" data-testid="billing-loading">
                <Skeleton className="h-12 w-64 bg-muted" />
                <div className="grid md:grid-cols-2 gap-6">
                    <Skeleton className="h-64 bg-muted" />
                    <Skeleton className="h-64 bg-muted" />
                </div>
            </div>
        );
    }

    const isAnnual = me.billing?.cycle === 'annual';
    const hasCustomer = Boolean(me.billing?.stripe_customer_id);
    const isPending = me.status === 'pending' || !me.is_active;
    const periodLabel = me.pricing.period_label; // "mois" | "an"

    return (
        <motion.div
            className="space-y-8"
            data-testid="billing-page"
            initial="hidden"
            animate="visible"
            variants={stagger}
        >
            {/* Header */}
            <motion.div variants={fadeIn}>
                <div className="flex items-center gap-3 mb-2">
                    <CreditCard className="w-7 h-7 text-[#681793]" />
                    <h1 className="text-3xl font-black text-white" data-testid="billing-title">
                        Facturation
                    </h1>
                </div>
                <p className="text-muted-foreground">
                    Gérez votre abonnement, vos factures et vos moyens de paiement.
                </p>
            </motion.div>

            {/* Pending banner */}
            {isPending && (
                <motion.div variants={fadeIn}>
                    <div
                        data-testid="subscription-pending-banner"
                        className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5 flex items-start gap-4"
                    >
                        <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
                            <Calendar className="w-5 h-5 text-amber-400" />
                        </div>
                        <div className="flex-1">
                            <h3 className="font-semibold text-white">Abonnement non actif</h3>
                            <p className="text-sm text-amber-100/80 mt-1">
                                Souscrivez à un abonnement pour activer tous les services Homly.
                            </p>
                            <Button
                                className="mt-3 bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white"
                                onClick={handleSubscribe}
                                disabled={creatingCheckout}
                                data-testid="subscribe-now-button"
                            >
                                {creatingCheckout ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirection...</>
                                ) : (
                                    <>Souscrire <ArrowRight className="w-4 h-4 ml-2" /></>
                                )}
                            </Button>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Main cards */}
            <div className="grid md:grid-cols-2 gap-6">
                {/* Current plan */}
                <motion.div variants={fadeIn}>
                    <div
                        className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden h-full"
                        data-testid="subscription-card"
                    >
                        <div className="p-6 border-b border-border">
                            <h2 className="text-lg font-bold text-white flex items-center gap-2">
                                <CreditCard className="w-5 h-5 text-[#FF914D]" />
                                Abonnement actuel
                            </h2>
                        </div>
                        <div className="p-6 space-y-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Plan</p>
                                    <p
                                        className="text-2xl font-bold text-white"
                                        data-testid="subscription-plan"
                                    >
                                        {isAnnual ? 'Annuel (-10%)' : 'Mensuel'}
                                    </p>
                                </div>
                                <Badge
                                    data-testid="subscription-status-badge"
                                    className={
                                        me.is_active
                                            ? 'bg-green-500/20 text-green-400 border-0'
                                            : 'bg-amber-500/20 text-amber-400 border-0'
                                    }
                                >
                                    {me.is_active ? 'Actif' : 'En attente'}
                                </Badge>
                            </div>

                            <div className="border-t border-border pt-6 space-y-3 text-sm">
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Période en cours</span>
                                    <span className="text-foreground" data-testid="subscription-period">
                                        {me.billing?.current_period_start
                                            ? `${formatDate(me.billing.current_period_start)} → ${formatDate(me.billing.current_period_end)}`
                                            : '—'}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Agence</span>
                                    <span className="text-foreground">{me.agency_name || '—'}</span>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-border">
                                <div className="flex items-center justify-between mb-6">
                                    <span className="font-medium text-white">
                                        Prochaine facture
                                    </span>
                                    <div className="text-right">
                                        <span
                                            className="text-3xl font-black bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent"
                                            data-testid="next-billing-amount"
                                        >
                                            {formatCurrency(me.next_billing_amount)}
                                        </span>
                                        <p className="text-xs text-muted-foreground">HT / {periodLabel}</p>
                                    </div>
                                </div>
                                {hasCustomer ? (
                                    <Button
                                        className="w-full bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white h-12"
                                        onClick={handleOpenPortal}
                                        disabled={openingPortal}
                                        data-testid="open-portal-button"
                                    >
                                        {openingPortal ? (
                                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ouverture...</>
                                        ) : (
                                            <>
                                                <ExternalLink className="w-4 h-4 mr-2" />
                                                Gérer mon abonnement
                                            </>
                                        )}
                                    </Button>
                                ) : (
                                    <Button
                                        className="w-full bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white h-12"
                                        onClick={handleSubscribe}
                                        disabled={creatingCheckout}
                                        data-testid="checkout-button"
                                    >
                                        {creatingCheckout ? (
                                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirection...</>
                                        ) : (
                                            <>Souscrire à un abonnement</>
                                        )}
                                    </Button>
                                )}
                                <p className="text-xs text-muted-foreground text-center mt-3">
                                    Paiement sécurisé via Stripe. Résiliable à tout moment.
                                </p>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Breakdown */}
                <motion.div variants={fadeIn}>
                    <div
                        className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden h-full"
                        data-testid="breakdown-card"
                    >
                        <div className="p-6 border-b border-border">
                            <h2 className="text-lg font-bold text-white">Détail de la facture</h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                Calcul automatique selon le nombre d'agents actifs.
                            </p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="p-4 rounded-xl bg-background/40 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-[#681793]/20 flex items-center justify-center">
                                        <Building2 className="w-5 h-5 text-[#681793]" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-white">Agence (compte admin)</p>
                                        <p className="text-xs text-muted-foreground">
                                            {formatCurrency(me.pricing.agency_unit)} HT / {periodLabel}
                                        </p>
                                    </div>
                                </div>
                                <span className="font-bold text-white">
                                    {formatCurrency(me.pricing.agency_unit)}
                                </span>
                            </div>

                            <div className="p-4 rounded-xl bg-background/40 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-[#FF914D]/20 flex items-center justify-center">
                                        <Users className="w-5 h-5 text-[#FF914D]" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-white">
                                            Agents{' '}
                                            <span className="text-muted-foreground" data-testid="agents-count">
                                                ({me.agents_count})
                                            </span>
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {formatCurrency(me.pricing.agent_unit)} HT / agent / {periodLabel}
                                        </p>
                                    </div>
                                </div>
                                <span className="font-bold text-white">
                                    {formatCurrency(me.agents_count * me.pricing.agent_unit)}
                                </span>
                            </div>

                            <div className="flex justify-between pt-4 border-t border-border">
                                <span className="font-semibold text-white">Total</span>
                                <span
                                    className="font-black text-xl bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent"
                                    data-testid="total-amount"
                                >
                                    {formatCurrency(me.total_current)} HT
                                </span>
                            </div>

                            <div className="p-3 rounded-lg bg-[#681793]/10 border border-[#681793]/20 text-xs text-foreground/80 flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                                <span>
                                    Inclus : IA Homly, espace vendeur, upload PDF, tous vos KPIs,
                                    accompagnement 30 jours.
                                </span>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Transactions */}
            <motion.div variants={fadeIn}>
                <div
                    className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden"
                    data-testid="transactions-card"
                >
                    <div className="p-6 border-b border-border flex items-center gap-2">
                        <Receipt className="w-5 h-5 text-[#FF914D]" />
                        <h2 className="text-lg font-bold text-white">Historique des factures</h2>
                    </div>
                    <div className="p-6">
                        {transactions.length === 0 ? (
                            <div
                                className="text-center py-12 text-muted-foreground"
                                data-testid="transactions-empty"
                            >
                                <Receipt className="w-10 h-10 mx-auto mb-3 opacity-40" />
                                <p>Aucune facture pour le moment.</p>
                                <p className="text-xs mt-1">
                                    Vos factures Stripe apparaîtront ici après votre premier paiement.
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm" data-testid="transactions-table">
                                    <thead>
                                        <tr className="text-left text-muted-foreground border-b border-border">
                                            <th className="pb-3 font-medium">Date</th>
                                            <th className="pb-3 font-medium">Type</th>
                                            <th className="pb-3 font-medium">Montant</th>
                                            <th className="pb-3 font-medium">Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transactions.map((tx, idx) => (
                                            <tr
                                                key={tx.session_id || tx.id || idx}
                                                className="border-b border-border/50 last:border-0"
                                                data-testid={`transaction-row-${idx}`}
                                            >
                                                <td className="py-3 text-foreground">
                                                    {tx.created_at ? formatDate(tx.created_at) : '—'}
                                                </td>
                                                <td className="py-3 text-muted-foreground capitalize">
                                                    {tx.billing_cycle === 'setup'
                                                        ? 'Installation'
                                                        : tx.billing_cycle === 'annual'
                                                        ? 'Abonnement annuel'
                                                        : 'Abonnement mensuel'}
                                                </td>
                                                <td className="py-3 font-medium text-white">
                                                    {formatCurrency(tx.amount || 0)}
                                                </td>
                                                <td className="py-3">
                                                    <Badge
                                                        className={
                                                            tx.payment_status === 'paid' ||
                                                            tx.status === 'paid'
                                                                ? 'bg-green-500/20 text-green-400 border-0'
                                                                : 'bg-amber-500/20 text-amber-400 border-0'
                                                        }
                                                    >
                                                        {tx.payment_status === 'paid' || tx.status === 'paid'
                                                            ? 'Payé'
                                                            : tx.payment_status || tx.status || 'En attente'}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {hasCustomer && (
                                    <div className="mt-4 text-center">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={handleOpenPortal}
                                            disabled={openingPortal}
                                            data-testid="open-portal-invoices-button"
                                        >
                                            <ExternalLink className="w-4 h-4 mr-2" />
                                            Voir toutes mes factures Stripe
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>

            {/* Quota IA — extension (upsell) */}
            <motion.div variants={fadeIn}>
                <AIQuotaCard />
            </motion.div>

            {/* Pricing reference */}
            <motion.div variants={fadeIn}>
                <div className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden">
                    <div className="p-6 border-b border-border">
                        <h2 className="text-lg font-bold text-white">Tarification Homly</h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Modèle simple : un abonnement par agence, tous les agents inclus.
                        </p>
                    </div>
                    <div className="p-6">
                        <div className="grid sm:grid-cols-3 gap-4">
                            <div className="text-center p-6 rounded-xl bg-gradient-to-br from-[#681793]/10 to-[#681793]/5 border border-[#681793]/20">
                                <p className="text-4xl font-black bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent">
                                    79€
                                </p>
                                <p className="text-sm text-muted-foreground">HT / mois</p>
                                <p className="font-medium text-white mt-3">Agence</p>
                                <p className="text-xs text-muted-foreground">
                                    Tous les agents et modules inclus
                                </p>
                            </div>
                            <div className="text-center p-6 rounded-xl bg-gradient-to-br from-[#FF914D]/10 to-[#FF914D]/5 border border-[#FF914D]/20">
                                <p className="text-4xl font-black bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent">
                                    249€
                                </p>
                                <p className="text-sm text-muted-foreground">HT / mois</p>
                                <p className="font-medium text-white mt-3">Vue Groupe</p>
                                <p className="text-xs text-muted-foreground">
                                    Multi-agences, en plus des abonnements agence
                                </p>
                            </div>
                            <div className="text-center p-6 rounded-xl bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20">
                                <p className="text-4xl font-black bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent">
                                    149€
                                </p>
                                <p className="text-sm text-muted-foreground">HT une fois</p>
                                <p className="font-medium text-white mt-3">Installation</p>
                                <p className="text-xs text-muted-foreground">
                                    Par agence et par groupe — config + campagne pub
                                </p>
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground text-center mt-6">
                            Engagement annuel : -10% sur l'abonnement. Option Agent Premium (Homly Réseau) : 19€ HT/mois, souscrite individuellement par l'agent.
                        </p>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}

export default BillingPage;
